import {Component, Inject, OnDestroy} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

import {Subscription} from 'rxjs';

import {
    ApiError,
    TradingAccountModel,
    TradingAccountReportModel,
    TradingService
} from '@spa/api';

import {Helper} from '../../../../shared/classes';
import {FormComponent} from '../../../../shared/widgets';
import {FormInterface} from '../../../../shared/interfaces';

import {
    DialogActionsEnum,
    DialogComponent,
    DialogConfigInterface,
    ReportTableConfigInterface,
    ReportTableData,
    ReportTableDataRow
} from '../../../../shared/components';

import {DisableAccountsForm} from '../../forms';
import {DisableAccountsFormErrorStatesEnum} from '../../enums';
import {TradingAccountsReducedObjectInterface} from '../../interfaces';

import {DisableAccountsMessages} from '../../../../../assets/messages';

import {
    DisableAccountsDataTableConfig,
    DisableAccountsDataTableItems
} from '../../../../../assets/descriptors';

@Component({
    selector: 'app-disable-accounts',
    templateUrl: './disable-accounts.component.html',
    styleUrls: ['./disable-accounts.component.scss']
})
export class DisableAccountsComponent extends DialogComponent<DisableAccountsComponent, number[]>
    implements FormInterface, OnDestroy {

    private disableAccountsSubscription: Subscription;
    private blockedAccountsIds: number[];

    public selectAll: boolean;
    public dataTableConfig: ReportTableConfigInterface;
    public disableAccountsForm: DisableAccountsForm;

    constructor(
        private tradingService: TradingService,
        @Inject(MAT_DIALOG_DATA) public data: {
            accounts: TradingAccountsReducedObjectInterface[],
            config: DialogConfigInterface
        },
        public dialogRef: MatDialogRef<DisableAccountsComponent>
    ) {
        super(dialogRef);

        this.config = data.config;
        this.blockedAccountsIds = [];
    }

    private createCashAccountDataItems(cashAccount: TradingAccountsReducedObjectInterface): ReportTableData[] {
        return DisableAccountsDataTableItems().map((dataTableItem: ReportTableData) => {
            switch (dataTableItem.colId) {
                case TradingAccountReportModel.getAccountReferenceIdSelector():
                    dataTableItem.value = cashAccount.reference;
                    break;
                case TradingAccountReportModel.getAccountTypeNameSelector():
                    dataTableItem.value = Helper.textsHelper().getTradingAccountTypeName(true);
                    break;
                case TradingAccountReportModel.getAccountStatusNameSelector():
                    dataTableItem.value = Helper.textsHelper().getCashAccountStatusName(cashAccount.status);
                    break;

            }

            return dataTableItem;
        });
    }

    private createTradingAccountDataItems(account: TradingAccountModel): ReportTableData[] {
        return DisableAccountsDataTableItems().map((dataTableItem: ReportTableData) => {
            switch (dataTableItem.colId) {
                case TradingAccountReportModel.getAccountReferenceIdSelector():
                    dataTableItem.value = account.accountReferenceId;
                    dataTableItem.shifted = true;
                    break;
                case TradingAccountReportModel.getAccountTypeNameSelector():
                    dataTableItem.value = Helper.textsHelper().getTradingAccountTypeLabel(false, account.subType);
                    break;
                case TradingAccountReportModel.getAccountStatusNameSelector():
                    dataTableItem.value = Helper.textsHelper().getTradingAccountStatusName(account.status);
                    break;

            }

            return dataTableItem;
        });
    }


    ngOnInit(): void {
        super.ngOnInit();

        this.dataTableConfig = DisableAccountsDataTableConfig();

        const rowsStack = this.data.accounts
            .map((cashAccount: TradingAccountsReducedObjectInterface, cashAccountIndex: number) => {
                let cashAccountRowIndex = cashAccountIndex;

                if (cashAccountIndex > 0) {
                    /**
                     * Get next index offset.
                     * The offset is the sum of all accounts length of all previous cash accounts.
                     */
                    for (let i = (cashAccountIndex - 1); i >= 0; i--) {
                        cashAccountRowIndex += this.data.accounts[i].accounts.length;
                    }
                }

                /**
                 * toggleable - if accounts array length without virtual(-1) is greater than 0
                 */
                const cashAccountRow = new ReportTableDataRow({
                    rowId: cashAccount.id,
                    rowIndex: cashAccountRowIndex,
                    isMainRow: true,
                    toggleable: (cashAccount.accounts.length > 0),
                    checkable: (cashAccount.status !== null),
                    dataItems: this.createCashAccountDataItems(cashAccount)
                });

                const accountsRows = cashAccount.accounts.map((account: TradingAccountModel, accountIndex: number) => {
                    return new ReportTableDataRow({
                        rowId: account.id,
                        rowIndex: (cashAccountRow.rowIndex + accountIndex + 1),
                        subRowOf: cashAccountRow.rowIndex,
                        dataItems: this.createTradingAccountDataItems(account),
                        checkable: true,
                        hidden: true
                    });
                });

                return accountsRows.concat(cashAccountRow);
            });

        this.dataTableConfig.rows = [].concat(...rowsStack)
            .sort((current: ReportTableDataRow, next: ReportTableDataRow) => current.rowIndex - next.rowIndex);

        this.initForm();
    }

    ngOnDestroy(): void {
        if (this.disableAccountsSubscription) {
            this.disableAccountsSubscription.unsubscribe();
        }
    }

    selectAllAccounts(checked: boolean): void {
        this.dataTableConfig.rows.map((row: ReportTableDataRow) => row.checked = checked);
    }

    getFormMessages(controlLabel?: string, controlMaxLength?: number): { [p: string]: string } {
        return FormComponent.getFormMessages(controlLabel);
    }

    initForm(): void {
        this.disableAccountsForm = new DisableAccountsForm(this.tradingService);

        this.disableAccountsForm.initForm();
    }

    validateForm(): boolean {
        return this.disableAccountsForm.validateForm(
            this.dataTableConfig.rows as ReportTableDataRow[],
            DisableAccountsMessages.AccountsForBlockingNotFound.message
        );
    }

    onFormSubmit(action?: number): void {
        switch (action) {
            case DialogActionsEnum.Decline:
                this.dialogRef.close(null);
                break;
            case DialogActionsEnum.ProcessFinished:
                this.dialogRef.close({
                    status: !this.config.process.failed,
                    data: this.blockedAccountsIds
                });

                break;
            case DialogActionsEnum.Confirm:
                if (this.validateForm()) {
                    this.startLoading(() => {
                        this.disableAccountsSubscription = this.disableAccountsForm
                            .createRequest(this.dataTableConfig.rows as ReportTableDataRow[])
                            .subscribe(this.dialogSubscribeHandler.bind(
                                this,
                                (data: number[]) => {
                                    this.blockedAccountsIds = data;
                                },
                                (errors: ApiError[]) => {
                                    this.config.process.failedMessage = errors.map((error: ApiError) => {
                                        switch (error.status) {
                                            case DisableAccountsFormErrorStatesEnum.BlockTradingAccountFailed:
                                                return DisableAccountsMessages.BlockingAccountGroupFailed.message
                                                    .replace('%%account%%', error.data);
                                            case DisableAccountsFormErrorStatesEnum.DisableAccountsRequestsNotFound:
                                                return DisableAccountsMessages.DisableAccountsRequestsNotFound.message;
                                        }
                                    }).join('</br>');
                                }
                            ));
                    });
                }
                break;
        }
    }
}
